import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { keyframes } from "@mui/system";

/**
 * ======================================================
 * ExamModals.jsx
 * ------------------------------------------------------
 * SUBJECT / POST-UTME / SINGLE EXAM ONLY
 *
 * Responsibilities:
 *  - Question Map
 *  - Confirm Submit
 *  - Success Modal (animated)
 * ======================================================
 */

/* ================= ANIMATION ================= */
const popIn = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  70% {
    transform: scale(1.15);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
`;

export default function ExamModals({
  /* Question Map */
  mapOpen,
  setMapOpen,
  questions = [],
  setIndexSafely,

  /* Confirm Submit */
  confirmSubmit,
  setConfirmSubmit,
  answers = {},
  handleSubmit,

  /* Success */
  successModal,
  navigate,
  examId, // ✅ FIX: properly received here
}) {
  const answeredSet = new Set(Object.keys(answers || {}));

  return (
    <>
      {/* ==================================================
          QUESTION MAP MODAL
         ================================================== */}
      <Dialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Question Map</DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            {questions.map((q, index) => {
              const isAnswered = answeredSet.has(String(q.id));

              return (
                <Grid item xs={3} key={q.id}>
                  <Button
                    fullWidth
                    variant={isAnswered ? "contained" : "outlined"}
                    color={isAnswered ? "success" : "primary"}
                    onClick={() => {
                      setIndexSafely(index);
                      setMapOpen(false);
                    }}
                  >
                    {index + 1}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================
          CONFIRM SUBMIT MODAL
         ================================================== */}
      <Dialog
        open={confirmSubmit}
        onClose={() => setConfirmSubmit(false)}
        disableEscapeKeyDown
        TransitionProps={{ unmountOnExit: true }}
      >
        <DialogTitle>Submit Exam?</DialogTitle>
        <DialogContent>
          <Typography>
            You have answered <b>{answeredSet.size}</b> question
            {answeredSet.size !== 1 ? "s" : ""}.
          </Typography>
          <Typography sx={{ mt: 1 }}>
            This will submit your exam and end the session.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmit(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleSubmit(false)}
          >
            Submit Exam
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================
          SUCCESS MODAL (ANIMATED)
         ================================================== */}
      <Dialog
        open={successModal && !confirmSubmit}
        TransitionProps={{ unmountOnExit: true }}
      >
        <DialogContent sx={{ textAlign: "center", px: 4, py: 3 }}>
          <CheckCircleIcon
            sx={{
              fontSize: 88,
              color: "#22c55e",
              mb: 1,
              animation: `${popIn} 0.45s ease-out`,
            }}
          />

          <Typography variant="h5" fontWeight={900} gutterBottom>
            Exam Completed Successfully
          </Typography>

          <Typography sx={{ opacity: 0.85, mb: 3 }}>
            Your answers have been submitted.
          </Typography>

          <Box display="flex" gap={2} justifyContent="center">
            <Button
              variant="outlined"
              sx={{ fontWeight: 800, minWidth: 150 }}
              onClick={() => navigate(`/dashboard/result/${examId}`)}
            >
              View Result
            </Button>

            <Button
              variant="contained"
              sx={{ fontWeight: 800, minWidth: 170 }}
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
